import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { Area } from '../../../../resources/area/area.interface';
import { Cluster } from '../../../../resources/cluster/cluster.interface';
import { Subject, combineLatest, zip, merge, race, concat } from 'rxjs';
import { Building } from '../../../../resources/building/building.interface';
import { Project } from '../../../../resources/project/project.interface';
import { BasicArea } from '../../../../resources/basic-area/basic-area.interface';
import { BuildingService } from '../../../../resources/building/building.service';
import { AreaService } from '../../../../resources/area/area.service';
import { BasicAreaService } from '../../../../resources/basic-area/basic-area.service';
import { ElementService } from '../../../../resources/element/element.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { RouterService } from '../../../../utilities/router/router.service';
import { ProjectService } from '../../../../resources/project/project.service';
import { takeUntil, distinctUntilChanged, zipAll } from 'rxjs/operators';
import { TranslateService } from '../../../../utilities/translate/translate.service';
import { DatePipe } from '@angular/common';
import { ConstantsService } from '../../../../utilities/constants/constants.service';
import { ConstantsQualityLevel } from '../../../../utilities/constants/constants.interface';

@Component({
  selector: 'sg2-basic-area-details-properties',
  templateUrl: './basic-area-details-properties.component.html',
  styleUrls: ['./basic-area-details-properties.component.less']
})
export class BasicAreaDetailsPropertiesComponent implements OnDestroy, OnInit {

  /** List of areas using the basic area as template. */
  public areas: Area[];

  /** Current basic area. */
  public basicArea: BasicArea;

  /** Form fields for patching basic area properties. */
  public basicAreaPropertiesForm: FormGroup;

  /** Destroy lifecycle hook. */
  public destroy$ = new Subject();

  /** Route params. */
  private params: {
    basicAreaId: BasicArea['_id'],
    projectId: Project['_id'],
  };

  /** Template source name. */
  public source: string;

  /** Quality levels. */
  public qualityLevels: ConstantsQualityLevel[];

  constructor(
    @Inject('$sgLocation') private $sgLocation: any,
    private buildingService: BuildingService,
    private areaService: AreaService,
    private basicAreaService: BasicAreaService,
    private constantsService: ConstantsService,
    private datePipe: DatePipe,
    private elementService: ElementService,
    private formBuilder: FormBuilder,
    private projectService: ProjectService,
    private routerService: RouterService,
    private translateService: TranslateService
  ) { }

  ngOnInit() {
    this.params = this.routerService.getParams();
    this.basicArea = this.basicAreaService.getBasicArea(this.params.basicAreaId);
    this.areas = this.basicAreaService.getConnectedAreas(this.basicArea);
    this.qualityLevels = this.constantsService.getQualityLevels();

    this._setBuildingIds();
    this._generateTemplateSourceName();
    this._initForm();
    this._onBasicAreaChange();
    this._onBasicAreaPropertiesChange();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Go to connected area.
   *
   * @todo Remove `/ng2` after new layout is set as default.
   */
  public goToArea($event, area: Area): void {
    $event.preventDefault();
    this.$sgLocation.path(`/project/${ this.params.projectId }/building/${ area.buildingId }/areas/${ area._id }`);
  }

  /**
   * Submit basic area properties form by keypress.
   *
   * After pressing enter, it simply blurs the form, causing the form observable to
   * pick up the blur event, and handles the patching.
   *
   * @param event Keyboard event.
   */
  public submitBasicAreaProperty(event: KeyboardEvent | any): void {
    event.target.blur();
  }

  /**
   * Generate template source name.
   */
  private _generateTemplateSourceName(): void {
    this.source = `${
      this.translateService.get('technicaldatabaseon')
    } ${
      this.datePipe.transform(this.basicArea.created, 'dd-MM-yyyy')
    }`;
  }

  /**
   * Initialize properties form.
   */
  private _initForm(): void {
    this.basicAreaPropertiesForm = this.formBuilder.group({
      areaType: { value: this.basicArea.templateId.name, disabled: true },
      qualityLevel: this.basicArea.qualityLevel,
      source: { value: this.source, disabled: true },
      areas: { value: 'numberOfAreas', disabled: false },
    }, { updateOn: 'blur' });
  }

  /**
   * Track changes on area.
   */
  private _onBasicAreaChange(): void {
    this.elementService.element$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.basicArea = this.basicAreaService.getBasicArea(this.params.basicAreaId);
    });
  }

  /**
   * Track changes on basic area properties form and patch area.
   *
   * Because the 'areas' list is not really part of form, it's not possible to globally track
   * changes. To get around that, each form field is tracked individually.
   */
  private _onBasicAreaPropertiesChange(): void {
    this.basicAreaPropertiesForm.get('qualityLevel').valueChanges.subscribe(qualityLevel => {
      this.basicAreaService.setQualityLevel(this.basicArea, qualityLevel);
    });
  }

  /**
   * Add the buildingId property to each area.
   *
   * @note The areas of one basicarea can be part of several buildings.
   */
  private _setBuildingIds(): void {
    this.areas.map(area => {
      area.buildingId = this.buildingService.getBuildingByAreaId(area._id)._id;
    });
  }
}
